import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  X,
  User,
  Mail,
  Phone,
  CreditCard,
  Shield,
  Briefcase,
  Calendar,
  Key,
  Copy,
  Eye,
  EyeOff,
  AlertCircle
} from 'lucide-react';
import { validateDocument, getValidationClass, getValidationMessage } from '../utils/validators';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function UserFormModal({ user, roles, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [showPasswords, setShowPasswords] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState('');
  const [passwordSuggestions, setPasswordSuggestions] = useState({
    system: '',
    email: ''
  });
  const [cpfTouched, setCpfTouched] = useState(false);
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cpf: '',
    phone: '',
    role: 'user',
    client_id: '',
    department: '',
    position: '',
    hire_date: '',
    systemPassword: '',
    emailPassword: ''   // ✅ Senha do email (editável)
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        cpf: user.cpf || '',
        phone: user.phone || '',
        role: user.role || 'user',
        status: user.status || 'pending',
        client_id: user.client_id || '',
        department: user.department || '',
        position: user.position || '',
        hire_date: user.hire_date ? user.hire_date.substring(0, 10) : ''
      });
    }
  }, [user]);

  // Carregar lista de clientes quando role = client
  useEffect(() => {
    if (formData.role === 'client' && clients.length === 0) {
      const token = localStorage.getItem('token');
      axios.get(`${API_URL}/api/clients`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setClients(r.data?.data || r.data || []))
        .catch(() => {});
    }
  }, [formData.role]);

  // ✅ Gera sugestão de email automaticamente ao digitar nome
  useEffect(() => {
    if (!user && formData.name && formData.role !== 'client') {
      const generateEmailSuggestion = () => {
        const parts = formData.name.trim().toLowerCase().split(' ');
        if (parts.length >= 2) {
          const firstName = parts[0];
          const lastName = parts[parts.length - 1];
          return `${firstName}.${lastName}@viploclocadora.com.br`;
        }
        return '';
      };

      const suggestion = generateEmailSuggestion();
      setEmailSuggestion(suggestion);

      // Se o campo de email estiver vazio, preenche com a sugestão
      if (!formData.email) {
        setFormData((prev) => ({ ...prev, email: suggestion }));
      }
    }
  }, [formData.name, formData.role, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const applyEmailSuggestion = () => {
    if (emailSuggestion) {
      setFormData((prev) => ({ ...prev, email: emailSuggestion }));
      toast.success('Sugestão de email aplicada!');
    }
  };

  // ✅ Gerar senha forte no frontend
  const generatePassword = (length = 12) => {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  // ✅ Gerar senhas automaticamente ao abrir modal (somente para novo usuário)
  useEffect(() => {
    if (!user) {
      const systemPwd = generatePassword(12);
      const emailPwd = generatePassword(16);
      setPasswordSuggestions({ system: systemPwd, email: emailPwd });
      setFormData((prev) => ({
        ...prev,
        systemPassword: systemPwd,
        emailPassword: emailPwd
      }));
    }
  }, [user]);

  // ✅ Aplicar sugestão de senha
  const applyPasswordSuggestion = (type) => {
    if (type === 'system') {
      setFormData((prev) => ({ ...prev, systemPassword: passwordSuggestions.system }));
    } else {
      setFormData((prev) => ({ ...prev, emailPassword: passwordSuggestions.email }));
    }
    toast.success('Sugestão de senha aplicada!');
  };

  // ✅ Gerar nova sugestão de senha
  const regeneratePassword = (type) => {
    const newPassword = generatePassword(type === 'system' ? 12 : 16);
    setPasswordSuggestions((prev) => ({ ...prev, [type]: newPassword }));
    setFormData((prev) => ({ ...prev, [`${type}Password`]: newPassword }));
    toast.success('Nova senha gerada!');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para área de transferência');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar CPF antes de enviar
    if (formData.cpf && !validateDocument(formData.cpf)) {
      toast.error('CPF inválido! Por favor, corrija antes de salvar.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      if (user) {
        // Update existing user
        await axios.put(
          `${API_URL}/api/users/management/${user.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Usuário atualizado com sucesso');
        onSuccess();
      } else {
        // Create new user
        const response = await axios.post(
          `${API_URL}/api/users/management`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setGeneratedData(response.data.data);
        toast.success(response.data.message);
        // Don't close modal yet - show generated credentials
      }
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    onSuccess();
  };

  // Show credentials screen after creation
  if (generatedData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-green-500 text-white p-6 rounded-t-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                ✅ Usuário Criado com Sucesso!
              </h2>
              <button
                onClick={handleFinish}
                className="text-white hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {/* User Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Informações do Usuário</h3>
              <div className="space-y-1 text-sm">
                <p><strong>Nome:</strong> {generatedData.name}</p>
                <p><strong>Email:</strong> {generatedData.email}</p>
                <p><strong>Perfil:</strong> {generatedData.role}</p>
              </div>
            </div>

            {/* Email Pending Alert */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-yellow-900 mb-2">
                    📧 EMAIL PENDENTE DE CRIAÇÃO
                  </h3>
                  <p className="text-sm text-yellow-800 mb-3">
                    O email <strong>{generatedData.email}</strong> ainda precisa ser criado no Hostinger.
                  </p>
                </div>
              </div>
            </div>

            {/* Credentials */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Credenciais Geradas
                </h3>
                <button
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                >
                  {showPasswords ? (
                    <><EyeOff className="w-4 h-4" /> Ocultar</>
                  ) : (
                    <><Eye className="w-4 h-4" /> Mostrar</>
                  )}
                </button>
              </div>

              <div className="space-y-4">
                {/* System Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🔑 Senha do Sistema (12 caracteres)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={generatedData.systemPassword}
                      readOnly
                      className="flex-1 px-3 py-2 border rounded-lg bg-gray-50 font-mono text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(generatedData.systemPassword)}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                      title="Copiar"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Será enviada para o usuário por email
                  </p>
                </div>

                {/* Email Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    📧 Senha do Email (16 caracteres)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={generatedData.emailPassword}
                      readOnly
                      className="flex-1 px-3 py-2 border rounded-lg bg-gray-50 font-mono text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(generatedData.emailPassword)}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                      title="Copiar"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Use esta senha ao criar o email no Hostinger
                  </p>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">📋 PRÓXIMOS PASSOS:</h3>
              <ol className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    1
                  </span>
                  <span>
                    Acesse: <a href="https://hpanel.hostinger.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://hpanel.hostinger.com</a>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    2
                  </span>
                  <span>Vá em <strong>"Emails"</strong> → <strong>"Contas de Email"</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    3
                  </span>
                  <span>
                    Criar conta: <strong>{generatedData.email}</strong>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    4
                  </span>
                  <span>
                    Usar a senha: <code className="bg-gray-200 px-1 rounded">{showPasswords ? generatedData.emailPassword : '••••••••••••••••'}</code>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    5
                  </span>
                  <span>Volte aqui e clique em <strong>"Confirmar Email Criado"</strong> na lista de usuários</span>
                </li>
              </ol>
            </div>

            {/* Action Button */}
            <div className="flex justify-end">
              <button
                onClick={handleFinish}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                Entendi, Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal form for create/edit
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-blue-500 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <User className="w-6 h-6" />
              {user ? 'Editar Usuário' : 'Novo Usuário'}
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome Completo *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="João Silva Santos"
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Email - Apenas para novo usuário */}
          {!user && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Corporativo *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="joao.silva@viploclocadora.com.br"
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {emailSuggestion && formData.email !== emailSuggestion && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="text-gray-600">Sugestão:</span>
                  <button
                    type="button"
                    onClick={applyEmailSuggestion}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {emailSuggestion}
                  </button>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Gerado automaticamente. Você pode editar se necessário.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* CPF */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CPF
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleChange}
                  onBlur={() => setCpfTouched(true)}
                  placeholder="123.456.789-00"
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg ${getValidationClass(formData.cpf, cpfTouched) || 'border-gray-300'}`}
                />
              </div>
              {cpfTouched && formData.cpf && (
                <p className={`text-xs mt-1 ${getValidationMessage(formData.cpf, cpfTouched).isValid ? 'text-green-600' : 'text-red-600'}`}>
                  {getValidationMessage(formData.cpf, cpfTouched).message}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(11) 98765-4321"
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Perfil *
            </label>
            <div className="relative">
              <Shield className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              >
                {roles.map((role) => (
                  <option key={role.name} value={role.name}>
                    {role.display_name} - {role.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Locatária — apenas para role = client */}
          {formData.role === 'client' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Locatária (empresa) *
              </label>
              <select
                name="client_id"
                value={formData.client_id}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione a locatária...</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.cnpj ? `- ${c.cnpj}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Este usuário terá acesso ao portal com faturas e frota dessa empresa.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Departamento
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder="Financeiro"
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                />
              </div>
            </div>

            {/* Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cargo
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  placeholder="Analista Financeiro"
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Status — apenas ao editar */}
          {user && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status da Conta
              </label>
              <select
                name="status"
                value={formData.status || 'pending'}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="pending">Pendente</option>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
                <option value="suspended">Suspenso</option>
              </select>
            </div>
          )}

          {/* Hire Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data de Admissão
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
              <input
                type="date"
                name="hire_date"
                value={formData.hire_date}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>

          {/* Senhas - Apenas para novo usuário */}
          {!user && (
            <div className="border-t pt-4 space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Key className="w-5 h-5" />
                Senhas (Editáveis)
              </h3>

              {/* System Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🔑 Senha do Sistema (12 caracteres)
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Key className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      name="systemPassword"
                      value={formData.systemPassword}
                      onChange={handleChange}
                      placeholder="Digite ou use a sugestão"
                      className="w-full pl-10 pr-4 py-2 border rounded-lg font-mono text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(formData.systemPassword)}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                    title="Copiar senha"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => regeneratePassword('system')}
                    className="px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm font-medium"
                    title="Gerar nova senha"
                  >
                    🔄
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Senha para acessar o sistema VIPLOC
                </p>
              </div>

              {/* Email Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📧 Senha do Email (16 caracteres)
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Key className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      name="emailPassword"
                      value={formData.emailPassword}
                      onChange={handleChange}
                      placeholder="Digite ou use a sugestão"
                      className="w-full pl-10 pr-4 py-2 border rounded-lg font-mono text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(formData.emailPassword)}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                    title="Copiar senha"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => regeneratePassword('email')}
                    className="px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm font-medium"
                    title="Gerar nova senha"
                  >
                    🔄
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Senha para acessar o email no Hostinger
                </p>
              </div>

              {/* Mostrar/Ocultar Senhas */}
              <button
                type="button"
                onClick={() => setShowPasswords(!showPasswords)}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
              >
                {showPasswords ? (
                  <><EyeOff className="w-4 h-4" /> Ocultar Senhas</>
                ) : (
                  <><Eye className="w-4 h-4" /> Mostrar Senhas</>
                )}
              </button>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Salvando...
                </div>
              ) : (
                user ? 'Atualizar' : 'Criar Usuário'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserFormModal;
